import type { ApolloError } from 'apollo-client';
import type { SeverityLevel } from '@sentry/vue';
import type { VueRouter } from 'vue-router/types/router';

import Vue from 'vue';
import BuildConfig from '../../env/build-config.json';

// Third-party domains whose network breadcrumbs add noise without diagnostic value
const IGNORED_DOMAINS = [
	'analytics.google.com',
	'adtrafficquality.google',
	'doubleclick.net',
	'yieldlove',
	'translate.goog',
];

// Firefox-specific errors that are almost always from third-party scripts
const SENTRY_IGNORED_ERRORS = [
	'NS_ERROR_NOT_INITIALIZED',
	'NS_ERROR_FAILURE',
	'NS_ERROR_NOT_AVAILABLE',
	'NS_ERROR_UNEXPECTED',
	'NS_ERROR_ABORT',
];

/** Init Config */
let dsn = JW_CONFIG.SENTRY_DSN;
if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
	dsn = JW_CONFIG.SENTRY_DSN_SSR_CLIENT;
}

let release = `jw-app:spa@${BuildConfig.commitShortHash}`;
if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
	release = `jw-app:ssr-client@${BuildConfig.commitShortHash}`;
}

/**
 * Sentry tags that can be set dynamically
 */
export interface SentryTags {
	auth_status?: 'logged_in' | 'logged_out';
	user_type?: 'seo' | 'returning';
	[key: string]: string | number | boolean | undefined;
}

// environment string à la: {prod|stage|canary|pr-canary}-{SSR|SPA}-{client|server}
function getEnvironment(xServeTrack?: string | null): string {
	const { env } = BUILD_CONFIG; // prod or stage
	const { BUILD_TARGET } = BUILD_CONFIG.globals; // SSR or SPA
	const platform = process.client ? 'client' : 'server';

	// Check for canary environment from xServeTrack header
	if (xServeTrack?.includes('canary')) {
		// Consolidate canaries to environment name e.g. canary-alpha
		return xServeTrack;
	}

	// Check for PR canary environment from config
	if (JW_CONFIG.PR_CANARY_FLAG != null) {
		return `pr-canary-${BUILD_TARGET}-${platform}`;
	}

	// Default to prod or stage environment
	return `${env}-${BUILD_TARGET}-${platform}`;
}

function getSampleRate(xServeTrack?: string | null) {
	// Sample all canary errors for the big5 - alpha > epsilon
	// Todo: consider including PR canaries in this
	// Canary-public samples at 10%
	if (xServeTrack?.includes('canary')) return 1.0;

	if (BUILD_CONFIG.env === 'prod') return 0.1;

	return 1.0;
}
/** Init Config */

/**
 * User properties to check for SSR hydration mismatches
 * Source: persist-state.plugin.ts persistUserProperties list
 */
const SSR_MISMATCH_PROPERTIES = [
	'accessToken',
	'hideSeenDislikedTitles',
	'isPremium',
	'userRole',
	'jwId',
	'jwLoginId',
	'lastUsedLoginProvider',
	'location',
	'loggedInProviders',
	'onboardingStatus',
	'persistedOverriddenLanguage',
	'persistedWebLocale',
	'settings',
	'newBuyboxTooltipAcknowledged',
	'loggedInNewBuyBoxView',
	'accountCreationTime',
	'showOldTitlePage',
] as const;

interface SentryOptions {
	router?: VueRouter;
	jwId?: string;
	xServeTrack: string | null;
	isSeoUser?: boolean;
	isLoggedIn?: boolean;
	buyboxView?: string;
	buyboxExpView?: string;
	buyboxPresentationType?: string;
	buyboxTooltipAcknowledged?: boolean;
	country?: string;
	newBuyboxFilterType?: string;
	onboardingStatus?: string;
}

const setupSentry = async (options: SentryOptions) => {
	if (!dsn) return false;

	const {
		router,
		jwId,
		xServeTrack,
		isSeoUser,
		isLoggedIn,
		buyboxView,
		buyboxExpView,
		buyboxPresentationType,
		buyboxTooltipAcknowledged,
		country,
		newBuyboxFilterType,
		onboardingStatus,
	} = options;
	const environment = getEnvironment(xServeTrack);
	const sampleRate = getSampleRate(xServeTrack);

	try {
		const Sentry = await import('@sentry/vue');

		const integrations = [];

		// Add Browser Tracing integration for performance monitoring
		if (router) {
			integrations.push(
				Sentry.browserTracingIntegration({
					router,
					routeLabel: 'name',
					idleTimeout: 1000,
					finalTimeout: 30000,
				})
			);
		}

		Sentry.init({
			Vue,
			dsn,
			environment,
			enabled: process.env.NODE_ENV !== 'development',
			integrations,
			maxValueLength: 10000,
			release,

			// Performance monitoring - at current rate of 3%, this would represent ~3.5mil transactions/mo
			tracesSampleRate: 0.03,

			// Here we can further filter the transactions we send to Sentry
			// This can limit the noise and costs of Sentry performance monitoring
			beforeSendTransaction: event => {
				const routeName = event.transaction;

				if (!routeName) return null;

				// Remove unnecessary routes
				if (routeName === 'app.refreshtoken') return null;
				// Home route gets a lot of traffic - in theory here we can sample it down to 50% of other pages
				if (routeName === 'app.home' && Math.random() < 0.5) return null;

				return event;
			},

			beforeSend: event => {
				const errorType = event.exception?.values?.[0]?.type || '';

				// ignore errors by name
				if (errorType && SENTRY_IGNORED_ERRORS.includes(errorType)) {
					return null;
				}

				// Drop errors from Google Translate mirrors and 3rd parties
				const pageUrl = event.request?.url ?? '';
				if (IGNORED_DOMAINS.some(domain => pageUrl.includes(domain))) {
					return null;
				}

				return event;
			},

			beforeBreadcrumb: breadcrumb => {
				const url = breadcrumb.data?.url;
				if (url && IGNORED_DOMAINS.some(domain => url.includes(domain))) {
					return null;
				}
				return breadcrumb;
			},

			// Error sampling
			sampleRate: sampleRate,
		});

		// Set user ID if provided
		if (jwId) {
			Sentry.setUser({ id: jwId });
		}

		// Set initial tags for user classification
		const tags: SentryTags = {};

		// User type: SEO (first-time) vs returning user
		if (isSeoUser !== undefined) {
			tags.user_type = isSeoUser ? 'seo' : 'returning';
		}

		// Auth status
		if (isLoggedIn !== undefined) {
			tags.auth_status = isLoggedIn ? 'logged_in' : 'logged_out';
		}

		// Country from language store
		if (country !== undefined) {
			tags.store_country = country;
		}

		// First page flag - starts as true, will be set to false on navigation
		tags.first_page = true;

		// Buybox preferences
		if (buyboxView !== undefined) {
			tags.initial_buybox_view = buyboxView;
			tags.buybox_view = buyboxView;
		}
		if (buyboxExpView !== undefined) {
			tags.initial_buybox_exp_view = buyboxExpView;
			tags.buybox_exp_view = buyboxExpView;
		}
		if (buyboxPresentationType !== undefined) {
			const type = buyboxPresentationType === null ? 'best' : buyboxPresentationType;
			tags.initial_buybox_presentation_type = type;
			tags.buybox_presentation_type = type;
		}
		if (buyboxTooltipAcknowledged !== undefined) {
			tags.initial_buybox_tooltip_acknowledged = buyboxTooltipAcknowledged;
			tags.buybox_tooltip_acknowledged = buyboxTooltipAcknowledged;
		}

		if (newBuyboxFilterType !== undefined) {
			tags.initial_new_buybox_filter_type = newBuyboxFilterType;
			tags.new_buybox_filter_type = newBuyboxFilterType;
		}

		if (onboardingStatus !== undefined) {
			tags.onboarding_status = onboardingStatus;
		}

		// SSR hydration debugging - detect mismatches between SSR and persisted state
		// We use __INITIAL_SSR_USER__ (snapshot) instead of __DATA__.state.user because it can be modified
		if (process.client && window.__INITIAL_SSR_USER__ && window.__INITIAL_SAVED_USER_STATE__) {
			const ssrUser = window.__INITIAL_SSR_USER__;
			const initialUserStore = window.__INITIAL_SAVED_USER_STATE__;

			tags.mismatch_buybox_presentation_type =
				ssrUser?.preferences?.buyboxPresentationType !== initialUserStore?.preferences?.buyboxPresentationType;

			tags.mismatch_new_buybox_view = ssrUser?.newBuyboxView !== initialUserStore?.newBuyboxView;

			tags.mismatch_buybox_exp_view = ssrUser?.buyboxExpView !== initialUserStore?.buyboxExpView;

			tags.mismatch_buybox_filter_type = ssrUser?.newBuyboxFilterType !== initialUserStore?.newBuyboxFilterType;

			tags.mismatch_jwId = ssrUser?.jwId !== initialUserStore?.jwId;

			// Check for any mismatch
			const hasPropMismatch = SSR_MISMATCH_PROPERTIES.some(prop => {
				const ssrValue = JSON.stringify((ssrUser as any)?.[prop]);
				const initialValue = JSON.stringify((initialUserStore as any)?.[prop]);
				return ssrValue !== initialValue;
			});
			tags.has_mismatch =
				hasPropMismatch ||
				Object.entries(tags).some(([key, value]) => key.startsWith('mismatch_') && value === true);
		}

		const initialUserCookieAvailability = window.__INITIAL_USER_COOKIE_AVAILABILITY__;
		const initialUserLocalStorageAvailability = window.__INITIAL_USER_LOCALSTORAGE_AVAILABILITY__;
		tags.initial_user_cookie_available = initialUserCookieAvailability;
		tags.initial_user_localstorage_available = initialUserLocalStorageAvailability;

		// Set initial tags
		if (Object.keys(tags).length > 0) {
			Sentry.setTags(tags);
		}

		return true;
	} catch (error) {
		console.error('Sentry initialization failed:', error);
		return false;
	}
};

/** Sentry Init State Machine */
enum STEPS {
	COLD = 0,
	INITIALIZING = 1,
	INITIALIZED = 2,
	FAILED = 3,
}
let INIT_STEP: 0 | 1 | 2 | 3 = STEPS.COLD;

export function initSentry(options: SentryOptions) {
	if (!process.client) return false;

	switch (INIT_STEP) {
		case STEPS.COLD:
			INIT_STEP = STEPS.INITIALIZING;
			setupSentry(options)
				.then(success => {
					INIT_STEP = success ? STEPS.INITIALIZED : STEPS.FAILED;
				})
				.catch(() => {
					INIT_STEP = STEPS.FAILED;
				});
			return true;
		case STEPS.INITIALIZING:
		case STEPS.INITIALIZED:
			return true;
		case STEPS.FAILED:
			return false;
	}
}

/** Sentry Init State Machine */
export type Primitive = number | string | boolean | bigint | symbol | null | undefined;

export async function captureMessageForSentry(
	name: string,
	payload: { [key: string]: any; error?: Error } = {},
	level: SeverityLevel = 'info',
	tags?: { [key: string]: Primitive }
) {
	if ((process.env.NODE_ENV === 'development' || JW_CONFIG.DOMAIN === 'moviecycle.com') && payload.error) {
		console.error(`${name}\n`, `${payload.where ?? ''}\n`, payload.error);
	}

	if (process.server && payload.error) {
		import('@sentry/node')
			.then(({ captureException }) => captureException(payload.error))
			.catch(() => console.error({ sentryError: payload.error, message: `${name}: ${payload.where ?? ''}` }));
		return;
	}

	// Check if Sentry is initialized
	if (INIT_STEP !== STEPS.INITIALIZED) return false;

	const { withScope, captureException, captureMessage } = await import('@sentry/vue');

	withScope(scope => {
		const error = payload['error'];
		const asException = level === 'error' && error;
		if (asException) {
			delete payload['error'];
		}

		scope.setExtra('1. version', process.env.VERSION);
		scope.setExtra('2. message', payload);

		if (error && 'graphQLErrors' in error) {
			const apolloError = error as ApolloError;
			if (apolloError.graphQLErrors.length) {
				scope.setExtra(
					'3. graphQLErrors',
					apolloError.graphQLErrors.map(e => ({
						message: e.message,
						path: JSON.stringify(e.path),
						locations: JSON.stringify(e.locations),
					}))
				);
			}

			if (apolloError.networkError) {
				scope.setExtra('4. networkError', {
					name: apolloError.networkError.name,
					message: apolloError.networkError.message,
					cause: apolloError.networkError.cause ?? 'unknown',
				});
			}
		}

		scope.setLevel(level);

		// set additional tags
		if (tags) scope.setTags(tags);

		if (asException) {
			if (error.message && Object.getOwnPropertyDescriptor(error, 'message')?.set != null) {
				error.message = [name, error!.message].join(' ');
			}
			captureException(error);
		} else {
			captureMessage(name);
		}
	});
}

/**
 * Update Sentry tags after initialization (non-blocking)
 * Useful for updating tags when user state changes (e.g., login, logout)
 */
export function updateSentryTags(tags: SentryTags) {
	if (!process.client) return;
	if (INIT_STEP !== STEPS.INITIALIZED) return;

	import('@sentry/vue')
		.then(({ setTags }) => {
			setTags(tags);
		})
		.catch(error => {
			console.error('Failed to update Sentry tags:', error);
		});
}
